import type { IconType } from "react-icons";

export interface WizardStep<K extends string> {
  key: K;
  icon: IconType;
}

export interface WizardStepNavProps {
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export interface WizardStepModel<K extends string, V> {
  keys: readonly K[];
  visibleSteps: (visibility: V) => ReadonlyArray<WizardStep<K>>;
  visibleKeys: (visibility: V) => K[];
  neighbors: (current: K, visibility: V) => { previous: K | undefined; next: K | undefined };
}

export const defineWizardSteps = <K extends string, V>(
  steps: ReadonlyArray<WizardStep<K>>,
  isVisible: (key: K, visibility: V) => boolean,
): WizardStepModel<K, V> => {
  const visibleSteps = (visibility: V) => steps.filter((step) => isVisible(step.key, visibility));
  const visibleKeys = (visibility: V) => visibleSteps(visibility).map((step) => step.key);

  return {
    keys: steps.map((step) => step.key),
    visibleSteps,
    visibleKeys,
    neighbors: (current, visibility) => {
      const keys = visibleKeys(visibility);
      const index = keys.indexOf(current);
      return {
        previous: index > 0 ? keys[index - 1] : undefined,
        next: index >= 0 && index < keys.length - 1 ? keys[index + 1] : undefined,
      };
    },
  };
};
